import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save, ArrowLeft, Type, Image as ImageIcon, Minus, Square, Variable,
  Copy, Trash2, ZoomIn, ZoomOut, Calendar
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import IconButton from '../components/IconButton';

// Fabric.js v6
import { Canvas, Textbox, Rect, Line, Image as FabricImage } from 'fabric';

// Conversão mm → pixels (96 DPI)
const MM_TO_PX = 3.7795;

const toPx = (mm) => (Number(mm) || 0) * MM_TO_PX;
const toMm = (px) => Math.round((Number(px) || 0) / MM_TO_PX);

// Formatação de data por extenso com dia da semana
const DIAS_SEMANA = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
const MESES_EXTENSO = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
];

// Interpreta 'YYYY-MM-DD' como data local (evita deslocamento por fuso horário)
const parseLocalDate = (value) => {
  if (!value) return new Date();
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? new Date() : d;
};

const formatDateExtenso = (value) => {
  const d = parseLocalDate(value);
  if (isNaN(d.getTime())) return '';
  return `${DIAS_SEMANA[d.getDay()]}, ${d.getDate()} de ${MESES_EXTENSO[d.getMonth()]} de ${d.getFullYear()}`;
};

// Tipos que renderizam texto (compartilham geometria e propriedades tipográficas)
const isTextLike = (type) => type === 'text' || type === 'variable' || type === 'date';

// Texto exibido no canvas para cada tipo
const getDisplayText = (el) => {
  if (el.type === 'variable') return `[${el.variable || 'variável'}]`;
  if (el.type === 'date') {
    return formatDateExtenso(el.dateMode === 'fixed' && el.dateValue ? el.dateValue : null);
  }
  return el.text || '';
};

const VARIABLES = {
  'Participante': [
    { key: 'participante.nome', label: 'Nome do participante' },
    { key: 'participante.email', label: 'E-mail' },
    { key: 'participante.celular', label: 'Celular' },
    { key: 'participante.cpf', label: 'CPF' }
  ],
  'Curso': [
    { key: 'curso.nome', label: 'Nome do curso' },
    { key: 'curso.facilitador', label: 'Facilitador' },
    { key: 'curso.data_inicio', label: 'Data de início' },
    { key: 'curso.data_fim', label: 'Data de término' },
    { key: 'curso.realiza', label: 'Instituição realizadora' },
    { key: 'curso.organiza', label: 'Instituição organizadora' },
    { key: 'curso.patrocinio', label: 'Patrocínio' },
    { key: 'curso.objetivo', label: 'Objetivo' },
    { key: 'curso.programa', label: 'Programa' },
    { key: 'curso.publico', label: 'Público-alvo' },
    { key: 'curso.curriculo', label: 'Currículo do facilitador' }
  ],
  'Emissão': [
    { key: 'emissao.dia', label: 'Dia da emissão' },
    { key: 'emissao.mes', label: 'Mês da emissão (extenso)' },
    { key: 'emissao.ano', label: 'Ano da emissão' },
    { key: 'emissao.data_completa', label: 'Data completa por extenso' }
  ]
};

const FONT_FAMILIES = ['Helvetica', 'Times-Roman', 'Courier', 'Georgia', 'Arial'];

const genId = () => `el_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const createElement = (type, overrides = {}) => {
  const base = {
    id: genId(),
    type,
    x: 50,
    y: 50,
    width: type === 'line' ? 100 : (type === 'variable' ? 80 : 100),
    height: type === 'line' ? 0 : (type === 'rect' ? 30 : 14),
    text: type === 'text' ? 'Texto' : '',
    variable: '',
    fontSize: 14,
    fontFamily: 'Helvetica',
    color: '#000000',
    bold: false,
    italic: false,
    align: 'left',
    lineHeight: 1.2,
    fillColor: '#ffffff',
    strokeColor: '#000000',
    strokeWidth: type === 'rect' || type === 'line' ? 1 : 0,
    opacity: 1,
    src: '',
    dateMode: 'current',
    dateValue: ''
  };
  return { ...base, ...overrides };
};

// Aplica as propriedades do elemento (modelo, em mm) em um objeto Fabric já existente
const applyElementToObject = (obj, el) => {
  const x = toPx(el.x);
  const y = toPx(el.y);
  const w = toPx(el.width);
  const h = toPx(el.height);
  const opacity = el.opacity !== undefined ? el.opacity : 1;

  if (isTextLike(el.type)) {
    const displayText = getDisplayText(el);
    const props = {
      left: x,
      top: y,
      width: w || 100,
      fontSize: el.fontSize || 14,
      fontFamily: el.fontFamily || 'Helvetica',
      fill: el.color || '#000000',
      fontWeight: el.bold ? 'bold' : 'normal',
      fontStyle: el.italic ? 'italic' : 'normal',
      textAlign: el.align || 'left',
      lineHeight: el.lineHeight || 1.2,
      opacity
    };
    // Não reescreve o texto enquanto o usuário está editando no canvas
    if (!obj.isEditing && obj.text !== displayText) props.text = displayText;
    obj.set(props);
  } else if (el.type === 'rect') {
    obj.set({
      left: x, top: y, width: w, height: h,
      fill: el.fillColor || '#ffffff',
      stroke: el.strokeColor || '#000000',
      strokeWidth: toPx(el.strokeWidth || 1),
      opacity
    });
  } else if (el.type === 'line') {
    obj.set({
      x1: x, y1: y, x2: x + w, y2: y + h,
      stroke: el.strokeColor || '#000000',
      strokeWidth: toPx(el.strokeWidth || 1),
      opacity
    });
  }
  obj.setCoords();
};

const CertModeloEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const fileInputRef = useRef(null);
  const objectsRef = useRef(new Map());       // elementId -> objeto Fabric
  const elementsRef = useRef([]);             // elementos da face atual
  const layoutsRef = useRef({ frente: [], verso: [] });
  const faceRef = useRef('frente');

  const [modelo, setModelo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [face, setFace] = useState('frente'); // 'frente' | 'verso'
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(0.7);

  // Atualiza estado + refs (fonte de verdade única)
  const commitElements = useCallback((next) => {
    elementsRef.current = next;
    layoutsRef.current[faceRef.current] = next;
    setElements(next);
  }, []);

  // Cria um objeto Fabric a partir do elemento (modelo)
  const buildObject = useCallback((el) => {
    const x = toPx(el.x);
    const y = toPx(el.y);
    const w = toPx(el.width);
    const h = toPx(el.height);
    const opacity = el.opacity !== undefined ? el.opacity : 1;
    let obj = null;

    if (isTextLike(el.type)) {
      obj = new Textbox(
        getDisplayText(el),
        {
          left: x, top: y, width: w || 100,
          fontSize: el.fontSize || 14,
          fontFamily: el.fontFamily || 'Helvetica',
          fill: el.color || '#000000',
          fontWeight: el.bold ? 'bold' : 'normal',
          fontStyle: el.italic ? 'italic' : 'normal',
          textAlign: el.align || 'left',
          lineHeight: el.lineHeight || 1.2,
          opacity,
          backgroundColor: el.type === 'variable' ? '#e0f2fe' : '',
          editable: el.type !== 'date'
        }
      );
    } else if (el.type === 'rect') {
      obj = new Rect({
        left: x, top: y, width: w, height: h,
        fill: el.fillColor || '#ffffff',
        stroke: el.strokeColor || '#000000',
        strokeWidth: toPx(el.strokeWidth || 1),
        opacity
      });
    } else if (el.type === 'line') {
      obj = new Line([x, y, x + w, y + h], {
        stroke: el.strokeColor || '#000000',
        strokeWidth: toPx(el.strokeWidth || 1),
        opacity
      });
    } else if (el.type === 'image' && el.src) {
      // Imagem: carrega de forma assíncrona e adiciona ao canvas quando pronta
      const img = new window.Image();
      img.onload = () => {
        const canvas = fabricRef.current;
        if (!canvas) return;
        const fimg = new FabricImage(img, { left: x, top: y });
        fimg.scaleX = (w || img.width) / img.width;
        fimg.scaleY = (h || img.height) / img.height;
        fimg.elementId = el.id;
        objectsRef.current.set(el.id, fimg);
        canvas.add(fimg);
        canvas.requestRenderAll();
      };
      img.src = el.src;
      return null;
    }

    if (obj) obj.elementId = el.id;
    return obj;
  }, []);

  // Reconstrói todos os objetos do canvas a partir da lista de elementos
  const buildObjects = useCallback((els) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    objectsRef.current = new Map();
    canvas.clear();
    canvas.backgroundColor = '#ffffff';
    els.forEach((el) => {
      const obj = buildObject(el);
      if (obj) {
        objectsRef.current.set(el.id, obj);
        canvas.add(obj);
      }
    });
    canvas.requestRenderAll();
  }, [buildObject]);

  // Lê a geometria dos objetos do canvas e atualiza os elementos
  const syncGeometry = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const next = elementsRef.current.map((el) => {
      const obj = objectsRef.current.get(el.id);
      if (!obj) return el;
      const updated = { ...el, x: toMm(obj.left), y: toMm(obj.top) };
      if (isTextLike(el.type)) {
        const w = obj.width * (obj.scaleX || 1);
        updated.width = toMm(w);
        obj.set({ width: w, scaleX: 1, scaleY: 1 });
      } else if (el.type === 'rect' || el.type === 'image') {
        const w = obj.width * (obj.scaleX || 1);
        const h = obj.height * (obj.scaleY || 1);
        updated.width = toMm(w);
        updated.height = toMm(h);
        obj.set({ width: w, height: h, scaleX: 1, scaleY: 1 });
      }
      return updated;
    });
    commitElements(next);
    canvas.requestRenderAll();
  }, [commitElements]);

  // Handler de edição de texto direto no canvas
  const handleTextChanged = useCallback((e) => {
    const obj = e?.target;
    if (!obj || !obj.elementId) return;
    const el = elementsRef.current.find((x) => x.id === obj.elementId);
    // Elementos de data não têm texto editável (o conteúdo é gerado)
    if (!el || el.type === 'date' || el.type === 'variable') return;
    const next = elementsRef.current.map((x) =>
      x.id === obj.elementId ? { ...x, text: obj.text } : x
    );
    commitElements(next);
  }, [commitElements]);

  // Carregar modelo
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/cert-modelos/${id}`);
        const m = res.data;
        layoutsRef.current = {
          frente: m.layout_frente?.elements || [],
          verso: m.layout_verso?.elements || []
        };
        setModelo(m);
        setLoading(false);
      } catch (err) {
        toast.error('Erro ao carregar modelo');
        navigate('/cert-modelos');
      }
    };
    load();
  }, [id, navigate]);

  // Inicializar canvas (uma vez por modelo)
  useEffect(() => {
    if (!canvasRef.current || !modelo) return;
    const w = (modelo.largura_m || 297) * MM_TO_PX;
    const h = (modelo.altura_m || 210) * MM_TO_PX;

    const canvas = new Canvas(canvasRef.current, {
      width: w,
      height: h,
      backgroundColor: '#ffffff',
      selection: true,
      preserveObjectStacking: true
    });
    fabricRef.current = canvas;

    const onSelection = () => {
      const obj = canvas.getActiveObject();
      setSelectedId(obj && obj.elementId ? obj.elementId : null);
    };
    canvas.on('selection:created', onSelection);
    canvas.on('selection:updated', onSelection);
    canvas.on('selection:cleared', () => setSelectedId(null));
    canvas.on('object:modified', () => syncGeometry());
    canvas.on('text:changed', handleTextChanged);

    const els = layoutsRef.current[faceRef.current] || [];
    commitElements(els);
    buildObjects(els);

    return () => {
      canvas.dispose();
      fabricRef.current = null;
      objectsRef.current = new Map();
    };
  }, [modelo, commitElements, buildObjects, syncGeometry, handleTextChanged]);

  // Aplicar zoom usando o zoom nativo do Fabric (mantém o clique alinhado)
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !modelo) return;
    const w = (modelo.largura_m || 297) * MM_TO_PX;
    const h = (modelo.altura_m || 210) * MM_TO_PX;
    // Ordem: redimensiona primeiro, depois aplica o zoom (setDimensions recria o contexto)
    canvas.setDimensions({ width: w * zoom, height: h * zoom });
    canvas.setZoom(zoom);
    canvas.requestRenderAll();
  }, [zoom, modelo]);

  // Trocar de face (frente/verso)
  useEffect(() => {
    if (!fabricRef.current) return;
    faceRef.current = face;
    const els = layoutsRef.current[face] || [];
    commitElements(els);
    buildObjects(els);
    setSelectedId(null);
  }, [face, commitElements, buildObjects]);

  // Add element
  const addElement = (type, overrides = {}) => {
    const el = createElement(type, overrides);
    const next = [...elementsRef.current, el];
    commitElements(next);
    const canvas = fabricRef.current;
    if (canvas) {
      const obj = buildObject(el);
      if (obj) {
        objectsRef.current.set(el.id, obj);
        canvas.add(obj);
        canvas.setActiveObject(obj);
        canvas.requestRenderAll();
        setSelectedId(el.id);
      }
    }
  };

  const addVariable = (variable) => {
    addElement('variable', { variable, text: variable });
  };

  // Update selected element properties (sem rebuild do canvas)
  const updateSelected = (updates) => {
    if (!selectedId) return;
    const next = elementsRef.current.map((el) =>
      el.id === selectedId ? { ...el, ...updates } : el
    );
    commitElements(next);
    const merged = next.find((el) => el.id === selectedId);
    const obj = objectsRef.current.get(selectedId);
    if (obj && merged) {
      applyElementToObject(obj, merged);
      fabricRef.current?.requestRenderAll();
    }
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    const next = elementsRef.current.filter((e) => e.id !== selectedId);
    commitElements(next);
    const canvas = fabricRef.current;
    const obj = objectsRef.current.get(selectedId);
    if (canvas && obj) {
      canvas.remove(obj);
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    }
    objectsRef.current.delete(selectedId);
    setSelectedId(null);
  };

  const duplicateSelected = () => {
    const el = elementsRef.current.find((e) => e.id === selectedId);
    if (!el) return;
    const copy = { ...el, id: genId(), x: (el.x || 0) + 10, y: (el.y || 0) + 10 };
    const next = [...elementsRef.current, copy];
    commitElements(next);
    const canvas = fabricRef.current;
    if (canvas) {
      const obj = buildObject(copy);
      if (obj) {
        objectsRef.current.set(copy.id, obj);
        canvas.add(obj);
        canvas.setActiveObject(obj);
        canvas.requestRenderAll();
        setSelectedId(copy.id);
      }
    }
  };

  // Image upload
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      addElement('image', { src: evt.target.result, width: 50, height: 50 });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Save
  const handleSave = async () => {
    setSaving(true);
    try {
      // Garante que a geometria atual do canvas esteja refletida no modelo
      syncGeometry();
      const data = face === 'frente'
        ? { layout_frente: { elements: layoutsRef.current.frente } }
        : { layout_verso: { elements: layoutsRef.current.verso } };
      await api.put(`/cert-modelos/${id}`, data);
      toast.success('Modelo salvo com sucesso!');
    } catch (err) {
      toast.error('Erro ao salvar modelo');
    } finally {
      setSaving(false);
    }
  };

  const selectedEl = elements.find((e) => e.id === selectedId);

  // Handler numérico que ignora campo vazio (evita NaN ao apagar)
  const numberHandler = (key) => (e) => {
    const v = e.target.value;
    if (v === '') return;
    updateSelected({ [key]: Number(v) });
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Carregando editor...</div>;

  return (
    <div className="flex flex-col h-screen bg-gray-100 dark:bg-gray-900">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <IconButton icon={ArrowLeft} onClick={() => navigate('/cert-modelos')} variant="secondary" size="sm" title="Voltar" />
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">{modelo?.nome_modelo}</h1>
            <p className="text-xs text-gray-500">Editor de Certificado</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Face toggle */}
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
            <button
              onClick={() => setFace('frente')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                face === 'frente' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Frente
            </button>
            <button
              onClick={() => setFace('verso')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                face === 'verso' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Verso
            </button>
          </div>
          <IconButton icon={ZoomOut} onClick={() => setZoom(z => Math.max(0.3, Math.round((z - 0.1) * 100) / 100))} variant="secondary" size="sm" title="Diminuir zoom" />
          <span className="text-sm text-gray-600 dark:text-gray-400 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <IconButton icon={ZoomIn} onClick={() => setZoom(z => Math.min(2, Math.round((z + 0.1) * 100) / 100))} variant="secondary" size="sm" title="Aumentar zoom" />
          <button
            onClick={() => navigate('/cert-modelos')}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium"
          >
            Fechar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left panel - Elements */}
        <div className="w-60 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto p-3 space-y-4">
          <div>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Elementos</h3>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => addElement('text')}
                className="flex flex-col items-center gap-1 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                <Type className="w-5 h-5 text-blue-600" />
                <span className="text-xs text-gray-700 dark:text-gray-300">Texto</span>
              </button>
              <button onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center gap-1 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                <ImageIcon className="w-5 h-5 text-green-600" />
                <span className="text-xs text-gray-700 dark:text-gray-300">Imagem</span>
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              <button onClick={() => addElement('rect')}
                className="flex flex-col items-center gap-1 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                <Square className="w-5 h-5 text-purple-600" />
                <span className="text-xs text-gray-700 dark:text-gray-300">Retângulo</span>
              </button>
              <button onClick={() => addElement('line')}
                className="flex flex-col items-center gap-1 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                <Minus className="w-5 h-5 text-amber-600" />
                <span className="text-xs text-gray-700 dark:text-gray-300">Linha</span>
              </button>
              <button onClick={() => addElement('date')}
                className="flex flex-col items-center gap-1 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                <Calendar className="w-5 h-5 text-rose-600" />
                <span className="text-xs text-gray-700 dark:text-gray-300">Data</span>
              </button>
            </div>
          </div>

          {/* Variables */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2 flex items-center gap-1">
              <Variable className="w-3.5 h-3.5" /> Variáveis
            </h3>
            {Object.entries(VARIABLES).map(([cat, vars]) => (
              <div key={cat} className="mb-3">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{cat}</p>
                <div className="space-y-1">
                  {vars.map((v) => (
                    <button key={v.key} onClick={() => addVariable(v.key)}
                      className="w-full text-left px-2 py-1.5 text-xs bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300 rounded hover:bg-cyan-100 dark:hover:bg-cyan-900/40 transition-colors truncate"
                      title={v.key}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Center - Canvas */}
        <div className="flex-1 overflow-auto bg-gray-300 dark:bg-gray-950 flex items-start justify-center p-8">
          <div className="shadow-2xl bg-white">
            <canvas ref={canvasRef} />
          </div>
        </div>

        {/* Right panel - Properties */}
        <div className="w-64 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto p-3">
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">Propriedades</h3>

          {!selectedEl ? (
            <p className="text-sm text-gray-400 text-center py-4">Selecione um elemento para editar</p>
          ) : (
            <div className="space-y-3">
              {/* Actions */}
              <div className="flex gap-1">
                <IconButton icon={Copy} onClick={duplicateSelected} variant="primary" size="sm" title="Duplicar" />
                <IconButton icon={Trash2} onClick={deleteSelected} variant="danger" size="sm" title="Excluir" />
              </div>

              {/* Type-specific */}
              {isTextLike(selectedEl.type) && (
                <>
                  {selectedEl.type === 'text' && (
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Texto</label>
                      <input className="input-sm w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm dark:text-white"
                        value={selectedEl.text}
                        onChange={(e) => updateSelected({ text: e.target.value })} />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Fonte</label>
                    <select className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm dark:text-white"
                      value={selectedEl.fontFamily}
                      onChange={(e) => updateSelected({ fontFamily: e.target.value })}>
                      {FONT_FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Tamanho</label>
                      <input type="number" className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm dark:text-white"
                        value={selectedEl.fontSize}
                        onChange={numberHandler('fontSize')} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Cor</label>
                      <input type="color" className="w-full h-8 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                        value={selectedEl.color}
                        onChange={(e) => updateSelected({ color: e.target.value })} />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => updateSelected({ bold: !selectedEl.bold })}
                      className={`flex-1 px-2 py-1.5 text-sm rounded border ${selectedEl.bold ? 'bg-blue-100 border-blue-500 text-blue-700' : 'border-gray-300 dark:border-gray-600'}`}>
                      <b>B</b>
                    </button>
                    <button onClick={() => updateSelected({ italic: !selectedEl.italic })}
                      className={`flex-1 px-2 py-1.5 text-sm rounded border ${selectedEl.italic ? 'bg-blue-100 border-blue-500 text-blue-700' : 'border-gray-300 dark:border-gray-600'}`}>
                      <i>I</i>
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Alinhamento</label>
                    <select className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm dark:text-white"
                      value={selectedEl.align}
                      onChange={(e) => updateSelected({ align: e.target.value })}>
                      <option value="left">Esquerda</option>
                      <option value="right">Direita</option>
                      <option value="center">Centralizado</option>
                      <option value="justify">Justificado</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Espaçamento de linha</label>
                    <input type="number" step="0.1" className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm dark:text-white"
                      value={selectedEl.lineHeight}
                      onChange={numberHandler('lineHeight')} />
                  </div>

                  {selectedEl.type === 'date' && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2">
                      <label className="block text-xs text-gray-600 dark:text-gray-400">Conteúdo da data</label>
                      <div className="flex gap-2">
                        <button onClick={() => updateSelected({ dateMode: 'current' })}
                          className={`flex-1 px-2 py-1.5 text-xs rounded border ${selectedEl.dateMode !== 'fixed' ? 'bg-blue-100 border-blue-500 text-blue-700' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
                          Data atual
                        </button>
                        <button onClick={() => updateSelected({ dateMode: 'fixed' })}
                          className={`flex-1 px-2 py-1.5 text-xs rounded border ${selectedEl.dateMode === 'fixed' ? 'bg-blue-100 border-blue-500 text-blue-700' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
                          Data específica
                        </button>
                      </div>
                      {selectedEl.dateMode === 'fixed' && (
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Data</label>
                          <input type="date" className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm dark:text-white"
                            value={selectedEl.dateValue || ''}
                            onChange={(e) => updateSelected({ dateValue: e.target.value })} />
                        </div>
                      )}
                      <p className="text-xs text-gray-400">
                        Prévia: {formatDateExtenso(selectedEl.dateMode === 'fixed' ? selectedEl.dateValue : null)}
                      </p>
                    </div>
                  )}
                </>
              )}

              {(selectedEl.type === 'rect') && (
                <>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Cor de fundo</label>
                    <input type="color" className="w-full h-8 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                      value={selectedEl.fillColor}
                      onChange={(e) => updateSelected({ fillColor: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Cor da borda</label>
                    <input type="color" className="w-full h-8 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                      value={selectedEl.strokeColor}
                      onChange={(e) => updateSelected({ strokeColor: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Espessura da borda (mm)</label>
                    <input type="number" step="0.1" className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm dark:text-white"
                      value={selectedEl.strokeWidth}
                      onChange={numberHandler('strokeWidth')} />
                  </div>
                </>
              )}

              {selectedEl.type === 'line' && (
                <>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Cor da linha</label>
                    <input type="color" className="w-full h-8 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                      value={selectedEl.strokeColor}
                      onChange={(e) => updateSelected({ strokeColor: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Espessura (mm)</label>
                    <input type="number" step="0.1" className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm dark:text-white"
                      value={selectedEl.strokeWidth}
                      onChange={numberHandler('strokeWidth')} />
                  </div>
                </>
              )}

              {/* Position (all types) */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Posição (mm)</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">X</label>
                    <input type="number" className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm dark:text-white"
                      value={selectedEl.x}
                      onChange={numberHandler('x')} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Y</label>
                    <input type="number" className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm dark:text-white"
                      value={selectedEl.y}
                      onChange={numberHandler('y')} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Largura</label>
                    <input type="number" className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm dark:text-white"
                      value={selectedEl.width}
                      onChange={numberHandler('width')} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Altura</label>
                    <input type="number" className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm dark:text-white"
                      value={selectedEl.height}
                      onChange={numberHandler('height')} />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Transparência: {Math.round((selectedEl.opacity ?? 1) * 100)}%</label>
                <input type="range" min="0" max="1" step="0.1" className="w-full"
                  value={selectedEl.opacity ?? 1}
                  onChange={(e) => updateSelected({ opacity: Number(e.target.value) })} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CertModeloEditor;
